import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, LogIn, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { site } from "@/config/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const WhatsAppGlyph = () => null; // no longer used in header

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full bg-white transition-shadow ${
        scrolled ? "border-b border-hairline" : "border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-site px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" aria-label="VaultSafe home">
          <Logo variant="light-bg" width={140} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 text-[15px] rounded-md transition-colors ${
                  isActive
                    ? "text-brand font-semibold"
                    : "text-ink-body hover:text-brand"
                }`
              }
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={site.customerPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 text-ink-body hover:text-brand px-3 py-2 text-sm font-medium transition-colors"
            data-testid="header-login"
          >
            <LogIn className="h-4 w-4" strokeWidth={1.75} />
            Customer Login
          </a>
          <Link
            to="/get-started"
            className="hidden sm:inline-flex items-center gap-2 rounded-md text-white px-5 py-2.5 text-sm font-semibold shadow-card-soft hover:shadow-card-lift hover:-translate-y-px transition-all"
            style={{ background: "linear-gradient(100deg,#1D6C35 0%,#2C994D 100%)" }}
            data-testid="header-cta-getstarted"
          >
            Get Protected
            <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
          </Link>
          <button
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-navy hover:bg-brand-card"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-hairline bg-white" data-testid="mobile-nav">
          <nav className="px-5 py-4 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `py-3 text-base border-b border-hairline last:border-b-0 ${
                    isActive ? "text-brand font-semibold" : "text-ink-body"
                  }`
                }
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/get-started"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-md text-white px-5 py-3 text-sm font-semibold"
              style={{ background: "linear-gradient(100deg,#1D6C35 0%,#2C994D 100%)" }}
              data-testid="mobile-cta-getstarted"
            >
              Get Protected
              <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
            </Link>
            <a
              href={site.customerPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-md border border-brand text-brand px-5 py-3 text-sm font-semibold"
              data-testid="mobile-login"
            >
              <LogIn className="h-4 w-4" />
              Customer Login
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
